import { useContext, useEffect, useMemo, useState } from "react";
import cn from "classnames";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Checkbox, Dropdown, Menu, notification, Tabs, Tooltip } from "antd";
import { Button, ButtonColorType, ButtonSize } from "@/ui-components/commonComponents/Button/Button";
import { useGetClientAssessmentInvitesStatsHook } from "@/modules/assessments/hooks/use-get-client-assessment-invites-stats.hook";
import AssessmentCompare from "@/modules/assessments/scenes/AssessmentCompare/AssessmentCompare";
import AssessmentReport from "@/modules/assessments/scenes/AssessmentReport/AssessmentReport";
import { capitalizeFirstLetter } from "@/shared/utils/common.utils";
import { getRoute } from "@/shared/utils/route/route.utils";
import { useAssessmentHook } from "@/shared/hooks/assessments/useAssessment.hook";
import { useRouteAccessController } from "@/shared/hooks/permissions/use-route-access-controller.hook";
import { Loading } from "@/shared/components/Loading/Loading";
import { Banner } from "@/ui-components/commonComponents/Banner/Banner";
import { SEO } from "@/ui-components/commonComponents/SEO/SEO";
import {
  ASSESSMENT_EDIT_INSIGHTS_ROUTE,
  ASSESSMENT_EDIT_COMPARE_ROUTE,
  ASSESSMENT_EDIT_INVITES_ROUTE,
  ASSESSMENT_EDIT_TESTS_ROUTE,
  ASSESSMENT_ROUTE,
  CLIENT_ROUTE,
  COMPETENCIES_ROUTE,
  SETTINGS_ROUTE,
} from "@/routing/AppRouter/routes.constants";
import { ReactComponent as AlertSVG } from "@/assets/icons/feather-icons/alert-triangle.svg";
import { ReactComponent as ChevronDownSVG } from "@/assets/icons/feather-icons/chevron-down.svg";
import { AddTests } from "../AddTests/AddTest";
import { AssessmentInsights } from "../AssessmentInsights/AssessmentInsights";
import { AssessmentInvites } from "../AssessmentInvites/AssessmentsInvites";
import { AssessmentSettings } from "../AssessmentSettings/AssessmentSettings";
import { AssessmentCompetencies } from "../AssessmentCompetencies/AssessmentCompetencies";
import { validateCompetencies } from "./utils/tabValidator";
import style from "./style.module.css";
import "../../styles/assessmentCommon.css";
import { AssessmentAbilityContext } from "@/shared/contexts/ability.context";
import { Action, Subject } from "@/shared/services/permissions/casl-ability.factory";
import { AssessmentHttpService } from "@/modules/assessments/services/assessment-http.service";
import { BasicModal } from "@/ui-components/commonComponents/Modal/BasicModal";
import { NextModalFooter } from "@/modules/candidate/assessment/CandidateAssessment/components/modals/ConfirmNextModal/NextModalFooter";
import { getFriendlyErrorMessage } from "@/shared/services/http";
import { downloadBlob, getFilenameFromContentDisposition } from "@/shared/utils/download.utils";
import { DEFAULT_LOCALE } from "@/shared/utils/locale.utils";

const { TabPane } = Tabs;

export function AssessmentEditHeader() {
  const {
    client_id: clientId,
    assessment_id: assessmentId,
    invite_id: inviteId,
    tab_type: activeTab,
  } = useParams<{
    client_id: string;
    assessment_id: string;
    invite_id: string;
    tab_type: string;
  }>();
  const DEFAULT_ASSESSMENT_PAGE_ROUTE = `/${CLIENT_ROUTE}/${clientId}/${ASSESSMENT_ROUTE}/${assessmentId}`;
  const [isActionLoading, setActionLoading] = useState(false);
  const [isArchiveModalOpen, setArchiveModalOpen] = useState(false);
  const navigate = useNavigate();
  const [archiveOptions, setArchiveOptions] = useState({ keepTeam: false });
  const assessmentAbility = useContext(AssessmentAbilityContext);
  const canManageAssessment = assessmentAbility.can(Action.Manage, Subject.ASSESSMENT);
  const canAddUserToAssessment = assessmentAbility.can(Action.Create, Subject.ASSESSMENT_USER_PERMISSION);
  const canViewInsights = assessmentAbility.can(Action.Read, Subject.ASSESSMENT_INVITE, "insights");
  const {
    clientAssessmentInvitesStats: { accepted, done, in_progress, invited, started, rescinded },
    isLoading: invitesStatsLoading,
  } = useGetClientAssessmentInvitesStatsHook(Number(assessmentId));
  const isEmpty = accepted + done + in_progress + invited + started + rescinded === 0;
  const [claimedQuantity, setClaimedQuantity] = useState(0);

  useEffect(() => {
    setClaimedQuantity(accepted + started + in_progress + done);
  }, [accepted, started, in_progress, done]);

  const { assessment, isLoading } = useAssessmentHook(Number(assessmentId));

  const handleArchiveAssessment = async () => {
    try {
      setArchiveModalOpen(false);
      setActionLoading(true);
      await AssessmentHttpService.archiveAssessmentByID(Number(clientId), Number(assessmentId), archiveOptions);
      navigate(`/${CLIENT_ROUTE}/${clientId}/${ASSESSMENT_ROUTE}`);
      notification.success({ message: "Successfully archived." });
    } catch (e) {
      console.error(`archive assessment error: ${e}`);
      notification.error({ message: getFriendlyErrorMessage(e) || "Failed to archive assessment." });
    } finally {
      setActionLoading(false);
    }
  };

  const handleExportAssessment = async () => {
    try {
      setActionLoading(true);
      const response = await AssessmentHttpService.exportAssessment(Number(clientId), Number(assessmentId));
      const filename =
        getFilenameFromContentDisposition(response.headers["content-disposition"]) ??
        `assessments_${assessmentId}_export_${new Date().getTime()}.csv`;

      downloadBlob(response.data, filename);
      notification.success({ message: "Successfully exported." });
    } catch (e) {
      notification.error({ message: getFriendlyErrorMessage(e) ?? "Oops, something went wrong. Please try again." });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveAssessment = async () => {
    try {
      setActionLoading(true);
      await AssessmentHttpService.removeAssessmentByID(Number(clientId), Number(assessmentId));
      navigate(`/${CLIENT_ROUTE}/${clientId}/${ASSESSMENT_ROUTE}`);
      notification.success({ message: "Successfully removed." });
    } catch (e) {
      console.error(`remove assessment error: ${e}`);
      notification.error({ message: getFriendlyErrorMessage(e) || "Failed to remove assessment." });
    } finally {
      setActionLoading(false);
    }
  };

  const areCompetenciesValid = useMemo(() => {
    if (!assessment?.assessmentTests) return false;

    return validateCompetencies(assessment.assessmentTests.map((test) => test.config.competencies));
  }, [assessment?.assessmentTests]);

  useRouteAccessController({
    slug: ASSESSMENT_EDIT_TESTS_ROUTE,
    hasPermission: canManageAssessment,
  });

  useRouteAccessController({
    slug: COMPETENCIES_ROUTE,
    hasPermission: canManageAssessment,
  });

  useRouteAccessController({
    slug: SETTINGS_ROUTE,
    hasPermission: canAddUserToAssessment,
  });

  // Default to permissive while assessment is loading (!assessment) so we don't
  // redirect users who *do* have access before the data arrives. Once loaded,
  // restrict to template-based assessments only.
  useRouteAccessController({
    slug: ASSESSMENT_EDIT_INSIGHTS_ROUTE,
    hasPermission: canViewInsights && (!assessment || Boolean(assessment.template)),
  });

  if (isLoading || !assessment || invitesStatsLoading || isActionLoading) {
    return <Loading />;
  }

  const isArchived = Boolean(assessment.archivedOn);
  const isFromTemplate = Boolean(assessment.template);
  const isChangeable = claimedQuantity === 0 && !isFromTemplate;
  const areTestsValid = assessment.assessmentTests?.length !== 0;
  // Assessment Templates reference tests that cannot be used in the current (legacy) way of scoring and reporting using competencies
  const isSendable = areTestsValid && (areCompetenciesValid || isFromTemplate);
  const isRemovable = isEmpty;

  return (
    <>
      <BasicModal
        autoHeight
        handleCancel={() => setArchiveModalOpen(false)}
        isModalOpen={isArchiveModalOpen}
        footer={<NextModalFooter handleOk={handleArchiveAssessment} handleCancel={() => setArchiveModalOpen(false)} />}
      >
        <div style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
          <p>Are you sure want to archive the assessment?</p>

          <div style={{ marginTop: "40px", marginBottom: "20px" }}>
            <Checkbox
              checked={archiveOptions.keepTeam}
              onClick={() => setArchiveOptions({ keepTeam: !archiveOptions.keepTeam })}
            >
              Keep Team?
            </Checkbox>
          </div>
        </div>
      </BasicModal>

      <SEO
        titleParams={[
          assessment.name[DEFAULT_LOCALE],
          capitalizeFirstLetter(String(activeTab === "invites" ? "Candidates" : activeTab)),
        ]}
      />

      <div className="main-container">
        <div className={style.wrapper}>
          <div className={style.assessmentEditHeader}>
            <Banner
              title={assessment?.name[DEFAULT_LOCALE]}
              description={String(assessment?.description) === "undefined" ? " " : String(assessment?.description)}
            >
              <div className={style.jobDescriptionContainer}>
                <div className={style.jobDescriptionComponent}>
                  <p>
                    <span>Company Job Title:</span> {assessment?.clientJobTitle?.[DEFAULT_LOCALE]}
                  </p>
                </div>
              </div>
            </Banner>
          </div>

          <Tabs
            type="card"
            defaultActiveKey={"settings"}
            activeKey={activeTab}
            className="tabs"
            tabBarExtraContent={
              canManageAssessment && (
                <Dropdown
                  trigger={["click"]}
                  overlay={
                    <Menu>
                      <Menu.Item key="archive" disabled={isArchived} onClick={() => setArchiveModalOpen(true)}>
                        Archive Assessment
                      </Menu.Item>
                      <Menu.Item key="export" disabled={isEmpty} onClick={handleExportAssessment}>
                        Export Assessment
                      </Menu.Item>
                      {/* Danger styling isn't necessary when the action isn't available. */}
                      <Menu.Item
                        key="remove"
                        disabled={!isRemovable}
                        danger={isRemovable}
                        onClick={handleRemoveAssessment}
                      >
                        Remove Assessment
                      </Menu.Item>
                    </Menu>
                  }
                >
                  <Button
                    colorType={ButtonColorType.transparentDark}
                    buttonSize={ButtonSize.XSmall}
                    className={style.actionsButton}
                    rightIcon={<ChevronDownSVG aria-hidden="true" />}
                  >
                    Assessment Actions
                  </Button>
                </Dropdown>
              )
            }
          >
            {canManageAssessment && (
              <>
                <TabPane
                  tab={
                    <Link
                      to={getRoute(DEFAULT_ASSESSMENT_PAGE_ROUTE, ASSESSMENT_EDIT_TESTS_ROUTE)}
                      className={style.tabTitleContainer}
                    >
                      <span className={cn(style.tabName, style.tabNameWithIcon)}>Tests</span>
                      <div className={style.tabIconContainer}>
                        {!areTestsValid && (
                          <Tooltip title="No tests have been added yet">
                            <span>
                              <AlertSVG />
                            </span>
                          </Tooltip>
                        )}
                      </div>
                    </Link>
                  }
                  key={ASSESSMENT_EDIT_TESTS_ROUTE}
                >
                  <AddTests isChangeable={isChangeable} isArchived={isArchived} />
                </TabPane>
                {!isFromTemplate && (
                  <TabPane
                    tab={
                      <Link
                        to={getRoute(DEFAULT_ASSESSMENT_PAGE_ROUTE, COMPETENCIES_ROUTE)}
                        className={style.tabTitleContainer}
                      >
                        <span className={cn(style.tabName, style.tabNameWithIcon)}>Competencies</span>
                        <div className={style.tabIconContainer}>
                          {!areCompetenciesValid && areTestsValid && (
                            <Tooltip title="There are test(s) with no turned on competencies">
                              <span>
                                <AlertSVG />
                              </span>
                            </Tooltip>
                          )}
                        </div>
                      </Link>
                    }
                    key={COMPETENCIES_ROUTE}
                  >
                    <AssessmentCompetencies disabled={!isChangeable || isArchived} />
                  </TabPane>
                )}
              </>
            )}
            {canAddUserToAssessment && (
              <TabPane
                tab={
                  <Link
                    to={getRoute(DEFAULT_ASSESSMENT_PAGE_ROUTE, SETTINGS_ROUTE)}
                    className={style.tabTitleContainer}
                  >
                    <span className={style.tabName}>Settings</span>
                  </Link>
                }
                key={SETTINGS_ROUTE}
              >
                <AssessmentSettings isArchived={isArchived} />
              </TabPane>
            )}
            <TabPane
              tab={
                <Link
                  to={getRoute(DEFAULT_ASSESSMENT_PAGE_ROUTE, ASSESSMENT_EDIT_INVITES_ROUTE)}
                  className={style.tabTitleContainer}
                >
                  <span className={style.tabName}>Candidates</span>
                </Link>
              }
              key={ASSESSMENT_EDIT_INVITES_ROUTE}
            >
              {inviteId ? (
                // Key by invite so candidate A -> B (a param-only route change) remounts the report,
                // resetting per-candidate state (Develop language, initial-load gate) instead of leaking.
                <AssessmentReport key={inviteId} />
              ) : (
                <AssessmentInvites
                  isSendable={isSendable}
                  stats={{ accepted, done, in_progress, invited, started, rescinded }}
                />
              )}
            </TabPane>
            {/* This feature is not available (yet) for template assessments. However, someone might navigate directly to this
            route if they're savvy with urls. This condition will render the component below, which has a nice empty state */}
            {(!isFromTemplate || activeTab === ASSESSMENT_EDIT_COMPARE_ROUTE) && (
              <TabPane
                disabled={done < 2}
                tab={
                  done < 2 ? (
                    <span className={style.tabName}>Compare</span>
                  ) : (
                    <Link
                      to={getRoute(DEFAULT_ASSESSMENT_PAGE_ROUTE, ASSESSMENT_EDIT_COMPARE_ROUTE)}
                      className={style.tabTitleContainer}
                    >
                      <span className={style.tabName}>Compare</span>
                    </Link>
                  )
                }
                key={ASSESSMENT_EDIT_COMPARE_ROUTE}
              >
                <AssessmentCompare />
              </TabPane>
            )}
            {canViewInsights && isFromTemplate && (
              <TabPane
                tab={
                  <Link
                    to={getRoute(DEFAULT_ASSESSMENT_PAGE_ROUTE, ASSESSMENT_EDIT_INSIGHTS_ROUTE)}
                    className={style.tabTitleContainer}
                  >
                    <span className={style.tabName}>Insights</span>
                  </Link>
                }
                key={ASSESSMENT_EDIT_INSIGHTS_ROUTE}
              >
                <AssessmentInsights />
              </TabPane>
            )}
          </Tabs>
        </div>
      </div>
    </>
  );
}
